'use client';

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PersistedVizPayload } from '@roomtreatment/core';

type SnapshotGetter = () => PersistedVizPayload | null;

interface VizSnapshotApi {
  register: (getter: SnapshotGetter | null) => void;
  getSnapshot: () => PersistedVizPayload | null;
  /** Bumps whenever the registered snapshot's underlying data changes (a new
   *  volumetric/raytrace/modal response lands). Reactive counterpart to the
   *  imperative `getSnapshot()` pull — lets a consumer (useAutoSave) notice
   *  "there's a fresher viz cube to persist" without polling. */
  vizVersion: number;
  /** Called by the viz producer (useModalViz) after a fetch lands new data. */
  markVizChanged: () => void;
}

const VizSnapshotContext = createContext<VizSnapshotApi | null>(null);

export function VizSnapshotProvider({ children }: { children: ReactNode }) {
  const getterRef = useRef<SnapshotGetter | null>(null);
  const [vizVersion, setVizVersion] = useState(0);
  const register = useCallback((getter: SnapshotGetter | null) => {
    getterRef.current = getter;
  }, []);
  const getSnapshot = useCallback(
    () => getterRef.current?.() ?? null,
    [],
  );
  const markVizChanged = useCallback(() => setVizVersion((v) => v + 1), []);
  return (
    <VizSnapshotContext.Provider value={{ register, getSnapshot, vizVersion, markVizChanged }}>
      {children}
    </VizSnapshotContext.Provider>
  );
}

export function useVizSnapshot(): VizSnapshotApi {
  const ctx = useContext(VizSnapshotContext);
  if (!ctx) {
    throw new Error('useVizSnapshot must be used within VizSnapshotProvider');
  }
  return ctx;
}
